import { processMatch } from "@/features/match/processMatch";
import { resetPlayerStats, updatePersonalBests } from "@/features/player/playerHelpers";
import type { Slice } from "@/store/createSlice";
import type { PersistedState } from "@/store/types";
import { DEFAULT_BANKROLL, DEFAULT_MULTIPLIER_CONFIG, emptyRecord } from "@/store/types";
import type {
  Activity,
  Bet,
  Match,
  MatchResult,
  MultiplierConfig,
  Player,
  StatusEvent,
} from "@/types";
import type { ActivityId, BetId, MatchId, MatchResultId, PlayerId } from "@/types/branded";

export const createConfigSlice: Slice<{
  lastStatusEvent: null;
  statusHistory: StatusEvent[];
  multiplierConfig: MultiplierConfig;
  updateMultiplierConfig: (config: Partial<MultiplierConfig>) => void;
  importState: (data: PersistedState) => void;
  resetState: () => void;
  resetScores: () => void;
  recalculateAll: () => void;
}> = (set, _get) => ({
  lastStatusEvent: null,
  statusHistory: [],
  multiplierConfig: DEFAULT_MULTIPLIER_CONFIG,

  updateMultiplierConfig: (config) =>
    set(
      (s) => {
        Object.assign(s.multiplierConfig, config);
      },
      false,
      "config/updateMultiplier",
    ),

  importState: (data) =>
    set(
      (s) => {
        s.players = data.players ?? emptyRecord<PlayerId, Player>();
        s.activities = data.activities ?? emptyRecord<ActivityId, Activity>();
        s.matches = data.matches ?? emptyRecord<MatchId, Match>();
        s.results = data.results ?? emptyRecord<MatchResultId, MatchResult>();
        s.bets = data.bets ?? emptyRecord<BetId, Bet>();
        s.lastStatusEvent = data.lastStatusEvent ?? null;
        s.statusHistory = Array.isArray(data.statusHistory) ? data.statusHistory : [];
        s.multiplierConfig = {
          ...DEFAULT_MULTIPLIER_CONFIG,
          ...(data.multiplierConfig ?? {}),
        };
      },
      false,
      "bulk/import",
    ),

  resetState: () =>
    set(
      (s) => {
        s.players = emptyRecord<PlayerId, Player>();
        s.activities = emptyRecord<ActivityId, Activity>();
        s.matches = emptyRecord<MatchId, Match>();
        s.results = emptyRecord<MatchResultId, MatchResult>();
        s.bets = emptyRecord<BetId, Bet>();
        s.lastStatusEvent = null;
        s.statusHistory = [];
        s.multiplierConfig = { ...DEFAULT_MULTIPLIER_CONFIG };
      },
      false,
      "bulk/reset",
    ),

  resetScores: () =>
    set(
      (s) => {
        s.matches = emptyRecord<MatchId, Match>();
        s.results = emptyRecord<MatchResultId, MatchResult>();
        s.bets = emptyRecord<BetId, Bet>();
        s.lastStatusEvent = null;
        s.statusHistory = [];
        for (const player of Object.values(s.players)) {
          resetPlayerStats(player, { resetBetting: true, bankroll: DEFAULT_BANKROLL });
        }
      },
      false,
      "bulk/resetScores",
    ),

  recalculateAll: () =>
    set(
      (s) => {
        // Reset all player stats
        for (const player of Object.values(s.players)) {
          resetPlayerStats(player);
        }

        // Clear old results
        s.results = emptyRecord<MatchResultId, MatchResult>();

        // Replay every match chronologically
        const allMatches = Object.values(s.matches).sort((a, b) => a.createdAt - b.createdAt);

        for (const match of allMatches) {
          const activity = s.activities[match.activityId];
          if (!activity) continue;

          const recentMatches = allMatches.filter((m) => m.createdAt < match.createdAt);

          const { updatedPlayers, result } = processMatch({
            match,
            players: s.players,
            activity,
            multiplierConfig: s.multiplierConfig,
            context: { recentMatches, playersById: s.players },
          });

          for (const p of updatedPlayers) {
            updatePersonalBests(p);
            s.players[p.id] = p;
          }
          s.results[result.id] = result;
        }
      },
      false,
      "bulk/recalculate",
    ),
});
